import { Component, OnInit, Optional, Self } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { interval, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

import { CanComponentDeactivate } from '@fry/components/common/form';
import { TrackOperationProgress, TracksOperationProgress } from '@fry/components/common';
import {
  BackendActionResult,
  Booking,
  UserBookingsStore,
  UserBookingStoreActionResult
} from '@fry/booking/lib/bookings';
import { ProfileStore } from '@fry/lib/profile';
import { EasBuilderTransition } from '@fry/components/common/form-builder/builder.interfaces';

import {
  Transaction,
  TransactionInternalReference,
  TransactionsStore,
  TransactionState
} from '@fry/payments/lib';
import { Venue, VenuesStore } from '@fry/booking/lib';
import { TransactionComponentEvent } from '@fry/payments/components';
import { BuilderStateService } from '@fry/components/common/form-builder/builder.state.service';
import { FileUploadControlComponentDelegate } from '@fry/components/common/file-upload-control';
import { EASButtonAction, EASLinkAction } from '@fry/components/common/buttons/buttons.service';
import { SecurityService } from '@fry/lib/security';
import {
  DialogComponent,
  DialogComponentData,
  DialogComponentService,
  DialogComponentStateFactory
} from '@fry/components/common/dialog';
import { LoaderLoadingState, LoaderReadyState, LoaderState } from '@fry/components/common/loader';

import * as Sentry from '@sentry/angular';
import {
  LocalNotificationsService,
  SENTRY_BOOKING_TAG,
  SENTRY_MODULE_TAG,
  SentryService
} from '@fry/lib/error';

import * as EASDialogError from '@fry/components/common/dialog/dialog.error';

import { parseISO } from 'date-fns';
import { BookingFileUploadDelegate } from './booking-file-upload.service';
import { BuilderUIFormService } from '@fry/components/common/form-builder/builder-ui-form.service';
import { NotifyService } from '@fry/lib/utils';
import { EasFormDialogComponent } from '@fry/components/common/easform/easform-dialog.component';
import { SYSTEM_ROLE_ID } from '@fry/lib/roles/roles.system';

const MODAL_DIALOG_STATES: {[K: string]: DialogComponentStateFactory} = {
    'canceled':
      () => DialogComponentData.canceled(),

    'error':
      (content) => DialogComponentData.error($localize `Submission error`, content),

    'error_validation':
      () => DialogComponentData.error('',
                                      $localize `There are some fields which require your attention.`),

    'generic':
      () => DialogComponentData.generic('', ''),

    'progress':
      () => DialogComponentData.progress($localize `Processing`,
                                         $localize `This may take a little while depending on your connection`),

    'confirmation':
      (title, description, okButton, cancelButton) => DialogComponentData.generic(title, description, okButton, cancelButton),

    'success':
      () => DialogComponentData.success()
};

@Component({
    selector: 'eas-booking-edit',
    templateUrl: './booking-edit.component.html',
    providers: [
        BuilderStateService,
        LocalNotificationsService,
        DialogComponentService,
        { provide: FileUploadControlComponentDelegate, useClass: BookingFileUploadDelegate },
    ],
    standalone: false
})
export class BookingEditComponent implements OnInit,
                                             CanComponentDeactivate,
                                             TracksOperationProgress {

  public booking: Booking;
  public bookingOption: any;
  // private workflow: any = {};
  public initialise$: Observable<any>;
  public transactions: Transaction[];
  public auditlog$: Observable<any[]>;
  public readOnlyGroup = false;

  public easFormGroup$ = this.builderState.easFormGroup$;
  public transitions$ = this.builderState.transitions$;
  public state$ = this.builderState.state$;

  public isOperationInProgress = false;
  public loadingState: LoaderState = LoaderLoadingState;

  public seatExpiration$: Observable<number>;
  public stateExpiration$: Observable<number>;
  private unsubscribe: Subject<void> = new Subject();

  public isDetailCollapsible = false;

  /* Sidebar */
  venue$: Observable<Venue>;
  seat: any;

  public buttons: (EASButtonAction | EASLinkAction)[] = [];

  constructor(
    public builderState: BuilderStateService,
    private builder: BuilderUIFormService,
    private bookings: UserBookingsStore,
    private dialog: MatDialog,
    @Self() private dialogComponentService: DialogComponentService,
    private notifications: LocalNotificationsService,
    private profile: ProfileStore,
    private route: ActivatedRoute,
    private router: Router,
    private security: SecurityService,
    private transactionsStore: TransactionsStore,
    private venues: VenuesStore,
    private fileUploadDelegate: FileUploadControlComponentDelegate,
    private notify: NotifyService,
    @Optional() private sentry: SentryService,
  ) {
    this.dialogComponentService.states = MODAL_DIALOG_STATES;
   }

  ngOnInit() {
    this.route.data.pipe(
      tap(data => this.booking = data.booking),
      switchMap(() => this.init())
    ).subscribe();
  }

  init() {
    this.notifications.removeAll();

    this.bookingOption = this.booking.bookingOption;
    // this.workflow = this.booking.workflow;
    this.fileUploadDelegate.setContext({ oid: this.booking.id });

    const subject: TransactionInternalReference = {
      id: this.booking.id, type: this.booking.doc.type
    };

    return this.transactionsStore.searchBySubject(subject).pipe(
      catchError(err => {
        console.log(err);
        return of([]);
      }),
      tap(transactions => this.transactions = transactions),
      switchMap(() => this.loadForm(this.bookingOption.form)),
      switchMap(() => {
        return this.requiredPayments$().pipe(
          catchError(err => {
            console.log(err);
            return of(false);
          })
        )
      }),
      tap(required => {
        this.isDetailCollapsible = required;
        this.isDetailsCollapsed(required);
      }),
      tap(() => this.initSidebar()),
      tap(() => {
        this.loadingState = LoaderReadyState;
      }),
      catchError(err => {
        this.loadingState = { state: 'error', error: err };
        return of(true);
      })
    );
  }

  isReady() {
    return !!this.bookingOption;
  }

  refresh(): Observable<any> {
    if (this.booking === undefined) {
      throw new Error($localize `Can't refresh without loaded booking!`);
    }
    return this.bookings.getWithDependencies(this.booking.id).pipe(
      tap(booking => this.booking = booking),
      switchMap(() => this.init())
    );
  }

  loadForm(form: any) {
    return this.profile.getCurrent().pipe(
      switchMap((user) => {
        return this.builderState.init(form,
                                      this.booking,
                                      this.booking.doc.data || {},
                                      this.booking.workflow,
                                      this.booking.owner,
                                      user);
      }),
      tap(() => {
        this.builderState.watchTransitions();
        this.readOnlyGroup = this.builderState.snapshot.group.readOnlyGroup;
        this.setupButtons();
      })
    );
  }

  private getCustomTransition(): Observable<EasBuilderTransition> {
    const payload = { destination: this.booking.doc.workflow.state };
    return this.builder.getCustomTransitionsForm(payload,
                                                 this.bookingOption.form)
               .pipe(
                 switchMap(group => {
                   const dialog = this.dialog.open(EasFormDialogComponent, {
                     data: {
                       title: $localize `Define transition`,
                       group,
                       onSubmit: (value) => of(value),
                     }
                  });

                  // Code above relies on this method returning an Observable
                  // therefore we must return `value` from here or throw it in
                  // case of dialog error.
                  return dialog.afterClosed().pipe(
                    tap((value) => {
                      if (value === undefined) {
                        throw EASDialogError.UserCanceledError();
                      }
                      if (EASDialogError.isDialogError(value)) { throw value; }
                    })
                  );
                })
    );
  }

  doCustomTransition(): Observable<Booking> {
    return this.getCustomTransition().pipe(
      switchMap(transition => {
        return this.builderState.collectTransitionData(transition, this.booking.doc).pipe(
          map(data => {
            return { transition, data };
          })
        );
      }),
      switchMap(data => {
        return this.bookings.customTransition(this.booking.id, data.transition, data.data);
      }),
      switchMap(() => this.refresh())
    );
  }

  setupButtons() {
    this.buttons = [
      // {
      //   type: 'link',
      //   label: $localize `Manage`,
      //   color: 'primary',
      //   condition: () => {
      //     return this.security.hasPermissionFor('bookings.manage', this.booking.doc.user, false, this.booking.id);
      //   },
      //   // condition: () => of(true),
      //   link: of(['../manage'])
      // },
      {
        type: 'button',
        label: $localize `Amend application`,
        color: 'warn',
        condition: () => this.security.hasRole(SYSTEM_ROLE_ID.ADMIN),
        click: () => {
          this.doCustomTransition().subscribe(
            () => this.notify.success($localize `Successfully applied`),
            (error) => {
              if (EASDialogError.isUserCanceledError(error)) { return; }
              this.notify.error(error);
            }
          );
        }
      },
      {
        type: 'button',
        label: $localize `Delete`,
        color: 'warn',
        condition: () => of(this.builderState.canDelete()).pipe(
          switchMap(canDelete => {
            if (canDelete) {
              return of(true);
            }
            return this.security.hasPermissionFor('bookings.delete', this.booking.doc.user);
          })
        ),
        click: () => {
          this.delete$().subscribe(
            () => this.router.navigate(['../..']),
          );
        }
      }
    ];
  }
  save() {
    this.notifications.removeAll();
    const value = this.builderState.getValue();
    this.bookings.update(this.booking.doc._id, this.booking.doc._rev, value).pipe(
      switchMap(() => this.refresh()),
    ).subscribe(
      () => {
        this.notifications.success($localize `Successfully saved`);
      },
      error => {
        this.notifications.error(error);
      }
     );
  }

  doTransition(transition: EasBuilderTransition) {
    this.notifications.removeAll();

    const group = this.builderState.snapshot.group;
    group.markAsTouched();

    if (!transition.allowInvalidForm && group.control.invalid) {
      const message = $localize `There are some fields which require your attention. Please review the form.`;
      this.notifications.warning(message);

      this.dialogComponentService.updateState(
        this.dialogComponentService.stateFor('error_validation')
      );

      return;
    }

    this.dialogComponentService.updateState(
      this.dialogComponentService.stateFor('progress')
    );

    // TODO: We probably need to revisit this and upgrade UBAlert so we can
    //       remove construction of message from here.
    this.doTransition$(transition).subscribe(
      (result) => {
        // This at the moment dismisses the dialog
        this.dialogComponentService.updateState(
          this.dialogComponentService.stateFor('success')
        );

        this.notifySuccess(result.result);

        if (result.booking === null) {
          this.router.navigate(['../..']);
        }
      },
      (error) => {
        if (EASDialogError.isUserCanceledError(error)) {
          this.dialogComponentService.updateState(
            this.dialogComponentService.stateFor('canceled')
          );
          return;
        }

        const message = typeof error === 'string' ? error : error.message;
        this.notifications.error(message);

        this.dialogComponentService.updateState(
          this.dialogComponentService.states['error'](message)
        );

        if (this.sentry) {
          this.sentry.withScope(scope => {
            scope.setLevel('error' as Sentry.SeverityLevel);
            scope.setTag(SENTRY_BOOKING_TAG, this.booking.id);
            scope.setTag(SENTRY_MODULE_TAG, 'booking');
            scope.captureMessage(message);
          });
        }
      }
    );
  }

  @TrackOperationProgress()
  private doTransition$(transition: EasBuilderTransition): Observable<UserBookingStoreActionResult> {
    const value = this.builderState.getValue();
    return this.bookings.update(this.booking.doc._id, this.booking.doc._rev, value)
                        .pipe(
                          switchMap(booking => {
                            this.booking = booking;
                            return this.builderState.collectTransitionData(transition,
                                                                           this.booking.doc);
                          }),
                          switchMap(data => {
                            return this.bookings.transition(this.booking.doc._id,
                                                            transition.id,
                                                            data);
                          }),
                          switchMap(result => {
                            if (result.result.status === 'deleted') {
                              return of(result);
                            }
                            return this.requiredPayments$().pipe(
                              catchError(err => {
                                console.log(err);
                                return of(false);
                              }),
                              tap(required => {
                                this.isDetailCollapsible = required;
                                this.isDetailsCollapsed(required);
                              }),
                              map(_ => result)
                            );
                          }),
                          switchMap(result => {
                            if (result.result.status === 'deleted') {
                              return of(result);
                            }
                            return this.refresh().pipe(map(_ => result));
                          }),

    );
  }

  @TrackOperationProgress()
  private delete$() {
    const dialog = this.dialog.open(DialogComponent, {
      hasBackdrop: true,
      closeOnNavigation: true,
      maxWidth: '27vw',
      data: {
        title: $localize `Remove booking`,
        content: $localize `If you really want to remove the booking, press OK`,
      }
    });

    return dialog.afterClosed().pipe(
      switchMap(result => {
        if (!result || (EASDialogError.isUserCanceledError(result))) { return of(); }
        return this.bookings.remove(this.booking.doc._id);
      }),
      catchError(error => {
        this.notifications.error(error);
        return throwError(error);
      })
    );
  }

  canSafelyDeactivate() {
    return true;
  }

  /* Sidebar */

  initSidebar() {
    if (this.bookingOption.venue && this.bookingOption.venue.venue) {
      this.venue$ = this.venues.get(this.bookingOption.venue.venue);
    }

    this.seat = this.booking.doc.metadata.seatConfirmation || {};
    this.auditlog$ = this.bookings.getAuditLog(this.booking.doc._id);

    if (this.booking.doc.metadata.seatConfirmation) {
      const till = parseISO(this.booking.doc.metadata.seatConfirmation.till);
      this.seatExpiration$ = interval(1000).pipe(
        startWith(0),
        takeUntil(this.unsubscribe),
        map(() => {
          const now = new Date();
          const val = (till.getTime() - now.getTime()) / 1000;
          if (val < -10) {
            this.unsubscribe.next();
          }
          return val > 0 ? val : 0;
          // return this.booking.doc.metadata.seatConfirmation.till;
        })
      );
    }

    if (this.booking.doc.workflow?.expiresAt) {
      const expiresAt = parseISO(this.booking.doc.workflow.expiresAt);
      this.stateExpiration$ = interval(1000).pipe(
        startWith(0),
        takeUntil(this.unsubscribe),
        map(() => {
          const now = new Date();
          const val = (expiresAt.getTime() - now.getTime()) / 1000;
          if (val < -10) {
            this.unsubscribe.next();
          }
          return val > 0 ? val : 0;
        })
      );
    }
  }

  /* Payments */

  /**
   * Payment component notification handler
   *
   * @param event Payment component event representing the notification.
   */
  onTransactionNotification(event: TransactionComponentEvent) {
    this.notifications.removeAll();

    switch (event.type) {
      case 'capture_success':
        this.refresh().subscribe(() => {
          this.notifications.success($localize `Payment successfully processed.`);
          console.log(`Booking Edit Component: Reloaded due to Transaction (${event.payload.transaction.role}) event: ${event.type}`);
          if (event.progress && event.progress.autotransition) {
            this.notifySuccess(event.progress.autotransition);
          }
        });
        break;
      case 'capture_error':
        const error = new Error($localize `We have been unable to process your payment`);
        this.notifications.error(error);

        if (this.sentry) {
          this.sentry.withScope(scope => {
            scope.setLevel('error' as Sentry.SeverityLevel);
            scope.setTag(SENTRY_BOOKING_TAG, this.booking.id);
            scope.setTag(SENTRY_MODULE_TAG, 'payments');
            const message = event.payload.transaction.outcome.details;
            scope.captureMessage(message);
          });
        }
        break;
      case 'state':
        break;
      case 'error':
        if (this.sentry) {
          this.sentry.withScope(scope => {
            scope.setLevel('info' as Sentry.SeverityLevel);
            scope.setTag(SENTRY_BOOKING_TAG, this.booking.id);
            scope.setTag(SENTRY_MODULE_TAG, 'payments');
            const message = event.payload.error.message;
            scope.captureMessage(message);
          });
        }
        break;
    }
  }

  requiredPayments$(): Observable<boolean> {
    // Calculate if we should display payment required warning
    const subject: TransactionInternalReference = {
      id: this.booking.id, type: this.booking.doc.type
    };

    return this.transactionsStore.searchBySubject(subject)
                                 .pipe(
      map(transactions => {
        // No transaction no thing is required
        if (transactions.length === 0) { return false; }

        // Calculate if all is paid
        return transactions.reduce((paid, transaction) => {
          if (!paid) { return paid; }
          return !transaction.isSettled;
        }, true);
      })
    );
  }

  private notifySuccess(result: BackendActionResult) {
    let message = result.message;
    if (result.detail) {
      message = `<strong>${message}</strong><br/>${result.detail.map(op => op.msg).join('<br/>')}`;
    }
    this.notifications.success(message);
  }

  //
  // Template helpers
  //
  private _isDetailsCollapsed = false;
  public isDetailsCollapsed(collapsed: boolean): boolean {
    if (collapsed !== undefined) {
      this._isDetailsCollapsed = collapsed;
    }
    return this._isDetailsCollapsed;
  }

  getBookingWindowLabel() {
    return this.bookingOption.state.id === 'window' ? $localize `Booking window open` : $localize `Booking window closed`;
  }

  protected readonly TransactionState = TransactionState;
}
